import { InsufficientCreditsAPIError, IsAPIError } from "@/constants/errors";
import { i18n } from "@/i18n";
import { CaseCreatePublicRequestWithTemplate, CaseOptions, CaseStatus } from "@/store/baseClientApi";
import { clientApi } from "@/store/clientApi";
import { CasesMapping, CaseState, ScanMapping } from "@/store/slices/casesSlice";
import { setLastFetched } from "@/store/slices/thumbnailsCacheSlice";
import { type AppDispatch, type RootState, store } from "@/store/store";
import { CaseStateData, DataObject } from "@/typed/CaseStateData";
import { ModelLabel } from "@/typed/ModelTypes";
import { scanObjectDataToProtobuf } from "@/utils/fileDataToProtobuf";
import { generateModelThumbnail } from "@/utils/generateModelThumbnail";
import { ensureError, localizeError } from "@/utils/helpers";
import { saveCaseThumbnailToIndexedDB, saveDataToIndexedDB, Store, ThumbnailDataObjectIndexedDB } from "@/utils/indexeddb";
import {
  deleteUploadFromStore,
  getUploadModelFromIndexedDB,
  getUploadScansFromIndexedDB,
  removeAndAddScansFromIndexedDB
} from "@/utils/uploadsCache";
import type { PayloadAction } from "@reduxjs/toolkit";
import { createAsyncThunk, createListenerMiddleware, createSelector, createSlice, isAnyOf } from "@reduxjs/toolkit";
import axios, { AxiosProgressEvent, AxiosRequestConfig } from "axios";
import toast from "react-hot-toast";

enum UploadStatus {
  Pending = 0,
  UploadingToDB = 1,
  AwaitingS3 = 2,
  UploadingToS3 = 3,
  AwaitingUploaded = 4,
  MarkingAsUploaded = 5,
  Complete = 6
}

interface CaseUpload {
  uploadId: number;
  caseId?: number;
  name: string;
  orgId: number;
  presignedURL?: string;
  progress: number;
  scans: ScanUpload[];
  status: UploadStatus;
  cancelled: boolean;
  error: boolean;
  templateId: number;
  retries: number;
  caseOptions: CaseOptions;
  unpaid: boolean;
}

interface TemplateOptions {
  id: number;
  caseOptions: CaseOptions;
}

interface ScanUpload {
  scanLabel: ModelLabel;
  filename: string;
  error: boolean;
}

interface CaseUploadsState {
  [index: number]: CaseUpload;
}

const initialState: CaseUploadsState = {};

// Store non-serialisable fields in a global constant - Scan files and thumbnails
const uploads: {
  // Cancel token is AbortController or RTK Query abort function
  [index: number]: { file: File; cancelToken?: AbortController | (() => void) };
} = {};

const UPLOAD_BATCH_SIZE = 5; // browser impose a per-domain limit of 6-8 connections

// Listener to handle updating state
const uploadsListener = createListenerMiddleware();
const startUploadListening = uploadsListener.startListening.withTypes<RootState, AppDispatch>();

// Type guard to check if error is AbortError or CanceledError
const isAbortError = (err: unknown) => {
  return err instanceof Object && "name" in err && (err?.name === "AbortError" || err?.name === "CanceledError");
};

const handleUploadCases = async (template: TemplateOptions, orgId: number, caseUploads: CasesMapping, scans: ScanMapping) => {
  const uploadPromises = Object.entries(caseUploads).map(async ([, caseUpload]) => {
    const caseState = caseUpload as CaseState;
    const caseData = await getUploadModelFromIndexedDB(caseState.uploadId);
    const uploadId = caseState.uploadId;

    const upperScanId = caseUploads[uploadId].upperScan;
    const lowerScanId = caseUploads[uploadId].lowerScan;
    const upperScan = upperScanId !== undefined ? caseData.scans.find((scan) => scan.id === upperScanId) : undefined;
    const lowerScan = lowerScanId !== undefined ? caseData.scans.find((scan) => scan.id === lowerScanId) : undefined;

    if (upperScan === undefined && lowerScan === undefined) {
      return;
    }

    // Create a new case object following the updated CaseUpload interface.
    const newCase: CaseUpload = {
      uploadId: uploadId,
      scans: [],
      status: UploadStatus.Pending,
      name: caseState.name,
      orgId: orgId,
      cancelled: false,
      error: false,
      templateId: template.id,
      caseOptions: template.caseOptions,
      retries: 0,
      progress: 0,
      unpaid: false
    };

    if (upperScan) {
      newCase.scans.push({
        scanLabel: "upper",
        filename: scans[upperScan.id].filename,
        error: false
      });
    }

    if (lowerScan) {
      newCase.scans.push({
        scanLabel: "lower",
        filename: scans[lowerScan.id].filename,
        error: false
      });
    }
    const fileData = await scanObjectDataToProtobuf("scan", upperScan, lowerScan);
    const upload = new File([fileData], "combined.pbtxt", {
      type: "application/octet-stream",
      lastModified: Date.now()
    });

    uploads[newCase.uploadId] = { file: upload };

    const scanData = await getUploadScansFromIndexedDB(uploadId);
    if (newCase.scans.length > 0 && scanData.length > 0) {
      // Remove unused scans
      const unselectedScans = scanData.filter((scan) => scan.id !== lowerScanId && scan.id !== upperScanId).map((scan) => scan.id);
      if (unselectedScans.length > 0) {
        await removeAndAddScansFromIndexedDB({ [uploadId]: unselectedScans });
      }

      // Create new case
      store.dispatch(addNewUploadCase(newCase));
    }
  });

  await Promise.allSettled(uploadPromises);
};

const handleCancelUpload = createAsyncThunk("uploads/cancelUpload", async (uploadId: number, { dispatch, getState }) => {
  const upload = (getState() as RootState).uploads[uploadId];
  if (upload === undefined) {
    return;
  }

  // abort requests
  const uploadData = uploads[uploadId];
  if (uploadData.cancelToken) {
    // If there's a cancel token, invoke it to cancel the request
    if (typeof uploadData.cancelToken === "function") {
      uploadData.cancelToken();
    } else {
      uploadData.cancelToken.abort();
    }
  }

  // delete case if it exists
  if (upload.caseId !== undefined) {
    dispatch(clientApi.endpoints.deleteCaseApiV1CasesCaseIdDelete.initiate({ caseId: upload.caseId }));
  }

  // cancel upload
  dispatch(caseUploadsSlice.actions.cancelUpload({ uploadId }));

  // delete upload from indexeddb data
  deleteUploadFromStore(upload.uploadId);

  // remove non-serialisable data from uploads
  delete uploads[upload.uploadId];

  // Remove uploads from state
  store.dispatch(cancelUpload({ uploadId }));
});

const handleCancelUploads = createAsyncThunk("uploads/cancelUploads", async (_, { dispatch, getState }) => {
  // stop and cancel all listeners
  uploadsListener.clearListeners();

  await Promise.allSettled(
    Object.values((getState() as RootState).uploads).map(async (upload?: CaseUpload) => {
      if (upload == null) {
        return;
      }

      await dispatch(handleCancelUpload(upload.uploadId));
    })
  );

  // Remove uploads from state
  store.dispatch(clearUploads());

  // restart listeners
  addListeners();
});

// Create redux slice
const caseUploadsSlice = createSlice({
  name: "uploads",
  initialState,
  reducers: {
    addNewUploadCase(state, action: PayloadAction<CaseUpload>) {
      // add new case
      state[action.payload.uploadId] = action.payload;
    },
    startDBUpload(state, action: PayloadAction<{ uploadId: number }>) {
      state[action.payload.uploadId].status = UploadStatus.UploadingToDB;
    },
    updateUploadCaseStatus(state, action: PayloadAction<{ uploadId: number; uploadStatus: UploadStatus }>) {
      // Update upload status
      const { uploadId, uploadStatus } = action.payload;
      const upload = state[uploadId];
      if (upload) {
        state[uploadId].status = uploadStatus;
      }
    },
    updateUploadCaseProgress(state, action: PayloadAction<{ uploadId: number; progress: number }>) {
      const { uploadId, progress } = action.payload;
      const upload = state[uploadId];
      if (upload) {
        state[uploadId].progress = progress;
      }
    },
    updateUploadCaseUnpaid(state, action: PayloadAction<{ uploadId: number; unpaid: boolean }>) {
      const { uploadId, unpaid } = action.payload;
      const upload = state[uploadId];
      if (upload) {
        state[uploadId].unpaid = unpaid;
      }
    },
    updateUploadCaseError(state, action: PayloadAction<{ uploadId: number; error: boolean }>) {
      // Set upload error
      const { uploadId, error } = action.payload;
      const upload = state[uploadId];
      if (upload) {
        state[uploadId].error = error;
      }
    },
    uploadCaseToDBSuccess(
      state,
      action: PayloadAction<{
        uploadId: number;
        caseId: number;
        presignedURL: string;
      }>
    ) {
      // uploaded to db successfully
      const { uploadId, caseId, presignedURL } = action.payload;
      const upload = state[uploadId];
      if (upload) {
        state[uploadId].caseId = caseId;
        state[uploadId].status = UploadStatus.AwaitingS3;
        state[uploadId].error = false;
        state[uploadId].presignedURL = presignedURL;
      }
    },
    uploadCaseToS3Success(
      state,
      action: PayloadAction<{
        uploadId: number;
        caseId: number;
      }>
    ) {
      // uploaded to S3 successfully
      const { uploadId } = action.payload;
      const upload = state[uploadId];
      if (upload) {
        state[uploadId].status = UploadStatus.AwaitingUploaded;
        state[uploadId].progress = 0;
      }
    },
    uploadMarkedUploadedSuccess(
      state,
      action: PayloadAction<{
        uploadId: number;
        caseId: number;
      }>
    ) {
      // successfully marked as uploaded - completed upload
      const { uploadId } = action.payload;
      const upload = state[uploadId];
      if (upload) {
        state[uploadId].status = UploadStatus.Complete;
      }
    },
    clearUploads() {
      return {};
    },
    cancelUpload(
      state,
      action: PayloadAction<{
        uploadId: number;
      }>
    ) {
      delete state[action.payload.uploadId];
    },
    recordRetry(state, action: PayloadAction<number>) {
      const uploadId = action.payload;
      if (state[uploadId]) {
        state[uploadId].retries += 1;
        state[uploadId].cancelled = false;
        state[uploadId].error = false;
        state[uploadId].presignedURL = undefined;
      }
    }
  },
  selectors: {
    selectIsUploading: (state) =>
      Object.values(state).some(
        (upload: CaseUpload) => upload != null && upload.status !== UploadStatus.Complete && !upload.error && !upload.cancelled
      ),
    selectHasUnpaidUploads: (state) => Object.values(state).some((upload: CaseUpload) => upload != null && upload.unpaid)
  }
});

const handleRetryUpload = createAsyncThunk("casesUploads/retryUpload", async (uploadId: number, { getState, dispatch }) => {
  const upload = (getState() as RootState).uploads[uploadId];
  if (upload === undefined) {
    return;
  }

  dispatch(updateUploadCaseError({ uploadId, error: false }));

  // record retry
  dispatch(caseUploadsSlice.actions.recordRetry(upload.uploadId));

  switch (upload.status) {
    case UploadStatus.UploadingToDB:
      // retry db upload
      dispatch(
        startDBUpload({
          uploadId: upload.uploadId
        })
      );
      return;
    case UploadStatus.UploadingToS3:
      if (upload.caseId === undefined) {
        break;
      }

      try {
        // fetch new presigned url
        const retryUpload = await dispatch(clientApi.endpoints.retryUploadCaseApiV1Put.initiate({ caseId: upload.caseId })).unwrap();

        // retry s3 upload
        dispatch(
          uploadCaseToDBSuccess({
            uploadId: upload.uploadId,
            caseId: upload.caseId,
            presignedURL: retryUpload.presigned_url
          })
        );
      } catch (_) {
        // retry retrying (this will trigger a retry of getting a new presigned url)
        dispatch(updateUploadCaseError({ uploadId, error: true }));
      }
      return;
    case UploadStatus.MarkingAsUploaded:
      if (upload.caseId === undefined) {
        break;
      }

      // retry marking as uploaded
      dispatch(
        uploadCaseToS3Success({
          uploadId: uploadId,
          caseId: upload.caseId
        })
      );
      return;
  }

  // unable to retry, cancel upload
  dispatch(handleCancelUpload(uploadId));
});

const addListeners = () => {
  // Listener for new uploads, create case in db
  startUploadListening({
    matcher: isAnyOf(
      caseUploadsSlice.actions.addNewUploadCase,
      caseUploadsSlice.actions.updateUploadCaseStatus,
      caseUploadsSlice.actions.updateUploadCaseError,
      caseUploadsSlice.actions.uploadMarkedUploadedSuccess,
      caseUploadsSlice.actions.cancelUpload
    ),
    effect: async (_, listenerApi) => {
      const uploads = listenerApi.getState().uploads;

      // Return if the number of concurrent uploads have reached the batch limit
      if (
        Object.values(uploads).filter((item) => {
          const upload = item as CaseUpload;

          return (
            upload.error === false &&
            upload.cancelled === false &&
            (upload.status === UploadStatus.UploadingToS3 || upload.status === UploadStatus.UploadingToDB)
          );
        }).length >= UPLOAD_BATCH_SIZE
      ) {
        return;
      }

      // Start next upload
      const nextUpload = Object.values(uploads)
        .sort((a, b) => compareUpload(a as CaseUpload, b as CaseUpload))
        .find((upload: CaseUpload) => upload.status === UploadStatus.Pending && !upload.error && !upload.cancelled) as CaseUpload;

      if (nextUpload) {
        listenerApi.dispatch(
          startDBUpload({
            uploadId: nextUpload.uploadId
          })
        );
      }
    }
  });

  startUploadListening({
    actionCreator: caseUploadsSlice.actions.startDBUpload,
    effect: async (action, listenerApi) => {
      const newCase = listenerApi.getState().uploads[action.payload.uploadId];

      if (newCase.scans.length <= 0) {
        return;
      }
      const caseContent = newCase.scans.length == 2 ? "combined" : newCase.scans[0].scanLabel;

      // upload case to db: create request args
      const caseCreateRequestArgs: CaseCreatePublicRequestWithTemplate = {
        name: newCase.name,
        case_content: caseContent,
        template_id: newCase.templateId
      };

      // build request
      const caseCreateRequest = listenerApi.dispatch(
        clientApi.endpoints.createCaseApiV1CasesPost.initiate({
          caseCreatePublicRequest: caseCreateRequestArgs
        })
      );
      // store abort controller
      uploads[newCase.uploadId].cancelToken = caseCreateRequest.abort;

      // execute request
      try {
        const caseResponse = await caseCreateRequest.unwrap();

        // update state with case id and new status
        listenerApi.dispatch(
          uploadCaseToDBSuccess({
            uploadId: newCase.uploadId,
            caseId: caseResponse.id,
            presignedURL: caseResponse.presigned_url || ""
          })
        );
      } catch (err) {
        if (!isAbortError(err)) {
          const error = ensureError(err);
          toast.error(localizeError(i18n.t, error));
        }
        // Set Error in State
        listenerApi.dispatch(
          updateUploadCaseError({
            uploadId: newCase.uploadId,
            error: true
          })
        );
      }
    }
  });

  const saveLocalThumbnails = async (caseId: number, caseState: CaseStateData | null, caseStatus: CaseStatus) => {
    // Copy thumbnails from uploadID store to caseId store
    let thumbnails = caseState?.thumbnails;
    if (!thumbnails) {
      thumbnails = [];
      // If the thumbnails weren't generated yet, make them now
      const models = caseState?.scans;
      if (models) {
        const thumbnailPromises = models.map(async (scan) => {
          const thumbnail = await generateModelThumbnail([scan]);
          return thumbnail;
        });

        // Wait for all thumbnails to be generated
        const newThumbnails = await Promise.all(thumbnailPromises);
        thumbnails.push(...newThumbnails);

        const upperScans = models.filter((scan) => scan.label === "upper");
        const lowerScans = models.filter((scan) => scan.label === "lower");

        const lastUpper = upperScans?.at(-1);
        const lastLower = lowerScans?.at(-1);

        if (lastUpper || lastLower) {
          const combinedThumbnail = await generateModelThumbnail([lastUpper, lastLower].filter((scan) => scan !== undefined));
          combinedThumbnail.label = "combined";
          thumbnails.push(combinedThumbnail);
        }
      }
    }
    if (!thumbnails) {
      return;
    }

    const thumbnailsToStore = thumbnails.filter((thumbnail) => thumbnail.label && ["upper", "lower", "combined"].includes(thumbnail.label));

    const dbData: ThumbnailDataObjectIndexedDB = {
      data: thumbnailsToStore,
      caseStatus,
      timestamp: Date.now()
    };

    if (thumbnailsToStore.length > 0) {
      await saveCaseThumbnailToIndexedDB(caseId.toString(), dbData, Store.ScanThumbnailStore);
    }
  };

  const saveLocalCase = async (caseId: number, uploadId: number, caseStatus: CaseStatus) => {
    const caseState = await getUploadModelFromIndexedDB(uploadId);
    saveLocalThumbnails(caseId, caseState, caseStatus);

    const models = caseState?.scans;
    if (models) {
      await saveDataToIndexedDB(caseId.toString(), models as DataObject[], Store.ScanStore);
    }
  };

  startUploadListening({
    actionCreator: caseUploadsSlice.actions.uploadCaseToDBSuccess,
    effect: async (action, listenerApi) => {
      // Part is now in DB, start upload to S3
      const newCase = action.payload;
      const s3AbortController = new AbortController();
      uploads[newCase.uploadId].cancelToken = s3AbortController;
      const fileName = `combined.pbtxt`;
      const file: File = new File([uploads[newCase.uploadId].file], fileName);

      // save case locally
      saveLocalCase(newCase.caseId, newCase.uploadId, "pending_upload");
      listenerApi.dispatch(setLastFetched({ id: newCase.caseId.toString() }));

      const config: AxiosRequestConfig = {
        signal: s3AbortController.signal,
        headers: {
          "x-amz-server-side-encryption": "aws:kms",
          "Content-Disposition": `attachment; filename="${file.name}"`
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          const progress = Math.min((progressEvent.loaded / (progressEvent.total ?? file.size)) * 100, 100);
          listenerApi.dispatch(caseUploadsSlice.actions.updateUploadCaseProgress({ uploadId: newCase.uploadId, progress }));
        }
      };
      if (newCase.presignedURL && newCase.caseId) {
        listenerApi.dispatch(
          updateUploadCaseStatus({
            uploadId: newCase.uploadId,
            uploadStatus: UploadStatus.UploadingToS3
          })
        );
        try {
          await axios.put(newCase.presignedURL, file, config);

          // Complete, update state status
          // update state with part id and new status
          listenerApi.dispatch(
            uploadCaseToS3Success({
              uploadId: newCase.uploadId,
              caseId: newCase.caseId
            })
          );
        } catch (err) {
          if (!isAbortError(err)) {
            const error = ensureError(err);
            toast.error(localizeError(i18n.t, error));
          }
          // Set Error in State
          listenerApi.dispatch(
            updateUploadCaseError({
              uploadId: newCase.uploadId,
              error: true
            })
          );
        }
      }
    }
  });

  // Listener for S3 completion
  startUploadListening({
    actionCreator: caseUploadsSlice.actions.uploadCaseToS3Success,
    effect: async (action, listenerApi) => {
      const newCase = action.payload;

      if (newCase.caseId === undefined) {
        return;
      }

      // Mark case as uploaded in db
      // Set state as 'MarkingAsUploaded'
      listenerApi.dispatch(
        updateUploadCaseStatus({
          uploadId: newCase.uploadId,
          uploadStatus: UploadStatus.MarkingAsUploaded
        })
      );

      // build request
      const caseMarkUploadedRequest = listenerApi.dispatch(
        clientApi.endpoints.markCaseUploadedApiV1CasesCaseIdUploadedPut.initiate({
          caseId: newCase.caseId
        })
      );
      // store abort controller
      uploads[newCase.uploadId].cancelToken = caseMarkUploadedRequest.abort;

      // execute request
      try {
        const caseResponse = await caseMarkUploadedRequest.unwrap();

        // update state with case id and new status
        listenerApi.dispatch(
          uploadMarkedUploadedSuccess({
            uploadId: newCase.uploadId,
            caseId: caseResponse.id
          })
        );
      } catch (err) {
        if (!isAbortError(err)) {
          const error = ensureError(err);
          if (IsAPIError(InsufficientCreditsAPIError, error)) {
            // Upload is complete but waiting for funds.
            // Continue as though it succeeded.
            listenerApi.dispatch(caseUploadsSlice.actions.updateUploadCaseUnpaid({ uploadId: newCase.uploadId, unpaid: true }));
            listenerApi.dispatch(
              uploadMarkedUploadedSuccess({
                uploadId: newCase.uploadId,
                caseId: newCase.caseId
              })
            );
            return;
          }
          toast.error(localizeError(i18n.t, error));
        }
        // Set Error in State
        listenerApi.dispatch(
          updateUploadCaseError({
            uploadId: newCase.uploadId,
            error: true
          })
        );
      }
    }
  });

  // Listener for upload completion (for clean-up)
  startUploadListening({
    actionCreator: caseUploadsSlice.actions.uploadMarkedUploadedSuccess,
    effect: async (action) => {
      const newCase = action.payload;

      // delete upload from indexeddb data as a case is now created
      await deleteUploadFromStore(newCase.uploadId);
      // remove non-serialisable data from uploads
      delete uploads[newCase.uploadId];
    }
  });
};

addListeners();

export const selectUploadingCases = createSelector([(state: RootState) => state.uploads], (state) =>
  Object.values(state).filter(
    (upload: CaseUpload) => upload.status === UploadStatus.Pending || upload.status === UploadStatus.AwaitingUploaded
  )
);

// Upload compare function that sorts uploads by upload ID and places completed uploads last.
export const compareUpload = (a: CaseUpload, b: CaseUpload) => {
  if (a.status === UploadStatus.Complete && b.status !== UploadStatus.Complete) {
    return 1;
  }
  if (a.status !== UploadStatus.Complete && b.status === UploadStatus.Complete) {
    return -1;
  }
  return a.uploadId - b.uploadId;
};

export const {
  addNewUploadCase,
  startDBUpload,
  updateUploadCaseStatus,
  uploadCaseToDBSuccess,
  updateUploadCaseError,
  uploadCaseToS3Success,
  uploadMarkedUploadedSuccess,
  clearUploads,
  cancelUpload
} = caseUploadsSlice.actions;

export const { selectIsUploading, selectHasUnpaidUploads } = caseUploadsSlice.selectors;

export { handleCancelUpload, handleCancelUploads, handleUploadCases, handleRetryUpload as retryUpload, uploadsListener, UploadStatus };
export type { CaseUpload, CaseUploadsState };

export default caseUploadsSlice.reducer;
// export default reducer as Reducer<CaseUploadsState>;
